<template>
  <div class="entry-builder">
    <b-row v-if="showConstructor">
      <b-col lg="6" class="mb-3">
        <section class="entry-side entry-side--debe">
          <header class="entry-side__header">
            <span class="entry-side__title">Debe</span>
            <span class="entry-side__total">{{ money(getTotalDebe) }}</span>
          </header>
          <div class="entry-side__form">
            <div class="entry-side__account" ref="debeAccount" :key="'form_debe_' + debe.account_id" @keydown.capture="onAccountKeydown($event, 'debe')">
              <FindObject render="search"
                          tag="Cuenta"
                          type="accountingAccountsImputation"
                          @select-object="loadDebeAccounting($event)"
                          :valueID="debe.account_id"
                          :where="conditionAccountingAccountsDebe" />
            </div>
            <b-form-group label="Importe" class="entry-side__amount">
              <b-input-group size="sm" prepend="$">
                <app-number-input ref="debeAmount" v-model="debe.amount" size="sm" @keydown.enter.prevent="addFromKeyboard('debe')" />
              </b-input-group>
            </b-form-group>
            <b-button size="sm" variant="dark" class="entry-side__add" v-b-tooltip.hover title="Agregar al Debe" @click="addDebe()">
              <AppIcon name="add" label="Agregar" />
            </b-button>
          </div>
          <div class="entry-side__list" v-if="debe.detail.length">
            <div class="entry-side__row" :class="{ 'entry-side__row--zero': !toNumber(item.amount) }" v-for="(item, index) in debe.detail" :key="'list_debe' + index">
              <span class="entry-side__name">
                <small v-if="item.account && item.account.code">{{ item.account.code }}</small>
                {{ item.account ? item.account.name : '' }}
              </span>
              <app-number-input v-if="editableAmounts" v-model="item.amount" size="sm" class="entry-side__input" @input="getAsiento()" />
              <b v-else class="entry-side__value">{{ money(item.amount) }}</b>
              <b-button v-if="!item.disabled" size="sm" variant="link" class="entry-side__remove" @click="removeDebe(index)">
                <AppIcon name="close" />
              </b-button>
            </div>
          </div>
          <p class="entry-side__empty" v-else>Todavía no hay cuentas en el Debe.</p>
        </section>
      </b-col>
      <b-col lg="6" class="mb-3">
        <section class="entry-side entry-side--haber">
          <header class="entry-side__header">
            <span class="entry-side__title">Haber</span>
            <span class="entry-side__total">{{ money(getTotalHaber) }}</span>
          </header>
          <div class="entry-side__form">
            <div class="entry-side__account" ref="haberAccount" :key="'form_haber_' + haber.account_id" @keydown.capture="onAccountKeydown($event, 'haber')">
              <FindObject render="search"
                          tag="Cuenta"
                          type="accountingAccountsImputation"
                          @select-object="loadHaberAccounting($event)"
                          :valueID="haber.account_id"
                          :where="conditionAccountingAccountsHaber" />
            </div>
            <b-form-group label="Importe" class="entry-side__amount">
              <b-input-group size="sm" prepend="$">
                <app-number-input ref="haberAmount" v-model="haber.amount" size="sm" @keydown.enter.prevent="addFromKeyboard('haber')" />
              </b-input-group>
            </b-form-group>
            <b-button size="sm" variant="dark" class="entry-side__add" v-b-tooltip.hover title="Agregar al Haber" @click="addHaber()">
              <AppIcon name="add" label="Agregar" />
            </b-button>
          </div>
          <div class="entry-side__list" v-if="haber.detail.length">
            <div class="entry-side__row" :class="{ 'entry-side__row--zero': !toNumber(item.amount) }" v-for="(item, index) in haber.detail" :key="'list_haber' + index">
              <span class="entry-side__name">
                <small v-if="item.account && item.account.code">{{ item.account.code }}</small>
                {{ item.account ? item.account.name : '' }}
              </span>
              <app-number-input v-if="editableAmounts" v-model="item.amount" size="sm" class="entry-side__input" @input="getAsiento()" />
              <b v-else class="entry-side__value">{{ money(item.amount) }}</b>
              <b-button v-if="!item.disabled" size="sm" variant="link" class="entry-side__remove" @click="removeHaber(index)">
                <AppIcon name="close" />
              </b-button>
            </div>
          </div>
          <p class="entry-side__empty" v-else>Todavía no hay cuentas en el Haber.</p>
        </section>
      </b-col>
    </b-row>

    <section class="entry-preview">
      <header class="entry-preview__header">
        <span class="entry-preview__title">{{ title }}</span>
        <span class="entry-preview__state entry-preview__state--info" v-if="isEmptyManual">Todavía no cargaste importes</span>
        <span class="entry-preview__state entry-preview__state--error" v-else-if="!validAsiento">Diferencia de {{ money(Math.abs(getTotalDebe - getTotalHaber)) }}</span>
        <span class="entry-preview__state entry-preview__state--ok" v-else>Asiento balanceado</span>
      </header>
      <b-table-simple class="app-table app-table--compact mb-0" small responsive v-if="previewDebe.length || previewHaber.length">
        <b-thead>
          <b-tr>
            <b-th class="text-left">Cuenta</b-th>
            <b-th width="20%" class="text-right">Debe</b-th>
            <b-th width="20%" class="text-right">Haber</b-th>
          </b-tr>
        </b-thead>
        <b-tbody>
          <b-tr v-for="(item, index) in previewDebe" :key="'entry_debe' + index">
            <b-td class="text-left align-middle">{{ item.account ? item.account.name : '' }}</b-td>
            <b-td class="text-right align-middle"><b>{{ money(item.amount) }}</b></b-td>
            <b-td></b-td>
          </b-tr>
          <b-tr v-for="(item, index) in previewHaber" :key="'entry_haber' + index">
            <b-td class="text-left align-middle"><span class="ml-4">{{ item.account ? item.account.name : '' }}</span></b-td>
            <b-td></b-td>
            <b-td class="text-right align-middle"><b>{{ money(item.amount) }}</b></b-td>
          </b-tr>
        </b-tbody>
        <b-tfoot>
          <b-tr class="entry-preview__totals">
            <b-td class="text-left">Totales</b-td>
            <b-td class="text-right">{{ money(getTotalDebe) }}</b-td>
            <b-td class="text-right">{{ money(getTotalHaber) }}</b-td>
          </b-tr>
        </b-tfoot>
      </b-table-simple>
    </section>
  </div>
</template>
<script>  
  import serviceAPI from './services'
  import ErrorToken from '@/handler/errorToken'
  import Helper from '@/handler/helper'
  import FindObject from '@/components/inc/find/findObject'  

  export default {
    props: {
      typeDebe: {
        type: String,
        default: '',
        required: true,
      },
      typeHaber: {
        type: String,
        default: '',
        required: true,
      }, 
      defaultDebe: {
        type: Array,
        default: [],
        required: true,
      },
      defaultHaber: {
        type: Array,
        default: [],
        required: true,
      },       
      currency: {
        type: Object,
        default: null
      },
      reference: {
        type: String,
        default: '',
        required: true,
      },
      amountTotal: {
        type: Number,
        default: 0,
        required: true,
      },          
      validationMode: {
        type: String,
        default: 'voucher',
      },
      showConstructor: {
        type: Boolean,
        default: false,
      },
      // Lista de cuentas para completar: admite importes en cero, que no pasan al asiento.
      editableAmounts: {
        type: Boolean,
        default: false,
      },
    },
    components: {
      FindObject,      
    },    
    data: () => {
      return { 
        debe: {
          account: null,
          account_id: 0,
          amount: 0,
          detail: [],
        },   
        haber: {
          account: null,
          account_id: 0,
          amount: 0,
          detail: [],
        },
        title: '',
      }
    }, 
    computed: {
      getCurrency() {
        if(this.currency) {
          return this.currency.name
        } else {
          return 'ARS'
        }        
      },

      conditionAccountingAccountsDebe(){
        if(this.typeDebe) {
          return [
            {field: 'active', condition: true},
            {field: 'type', condition: this.typeDebe}
          ];
        } else {
          return [
            {field: 'active', condition: true},
          ]
        }
      },        
      conditionAccountingAccountsHaber(){
        if(this.typeHaber) {
          return [
            {field: 'active', condition: true},
            {field: 'type', condition: this.typeHaber}
          ];
        } else {
          return [
            {field: 'active', condition: true},
          ]
        }
      },

      previewDebe() {
        return this.debe.detail.filter(element => parseFloat(element.amount))
      },
      previewHaber() {
        return this.haber.detail.filter(element => parseFloat(element.amount))
      },
      getTotalDebe() {
        var sum = 0
        this.debe.detail.forEach(element => {
          sum = sum + (parseFloat(element.amount) || 0)
        });
        return sum        
      },
      getTotalHaber() {
        var sum = 0
        this.haber.detail.forEach(element => {
          sum = sum + (parseFloat(element.amount) || 0)
        });
        return sum        
      },
      isEmptyManual() {
        return this.validationMode == 'manual' && !this.getTotalDebe && !this.getTotalHaber
      },
      validAsiento() {
        if(this.validationMode == 'manual') {
          return parseFloat(this.getTotalDebe.toFixed(2)) > 0 &&
                 parseFloat(this.getTotalDebe.toFixed(2)) == parseFloat(this.getTotalHaber.toFixed(2))
        }

        return parseFloat(this.getTotalDebe.toFixed(2)) == parseFloat(this.amountTotal.toFixed(2)) &&
               parseFloat(this.getTotalHaber.toFixed(2)) == parseFloat(this.amountTotal.toFixed(2))
      }
    },   
    mounted() {
      this.titleEntrie()
      this.loadAsiento()
    }, 
    watch: {
      reference() {
        this.titleEntrie()
      }
    },
    methods: {
      // El buscador se vuelve a crear al elegir y su modal devuelve el foco al cerrarse: se espera a que termine.
      focusAccount(side) {
        setTimeout(() => {
          const input = this.$refs[side + 'Account'] && this.$refs[side + 'Account'].querySelector('input')
          if(input) input.focus()
        }, 400)
      },
      // Con la cuenta elegida, Tab saltea los botones del buscador y va al importe.
      onAccountKeydown(event, side) {
        if(event.key != 'Tab' || event.shiftKey || !this[side].account_id || event.target.tagName != 'INPUT') return
        event.preventDefault()
        this.$refs[side + 'Amount'].$el.focus()
      },
      addFromKeyboard(side) {
        const before = this[side].detail.length
        if(side == 'debe') {
          this.addDebe()
        } else {
          this.addHaber()
        }
        if(this[side].detail.length > before) {
          this.focusAccount(side)
        }
      },
      toNumber(value) {
        return parseFloat(value) || 0
      },
      money(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency:this.getCurrency}).format(value || 0)
      },
      titleEntrie() {
        this.title = 'Asiento Contable: ' + this.reference
      },

      loadDebeAccounting(object) {
        if(object){
          this.debe.account = object
          this.debe.account_id = object.id
          this.focusAccount('debe')
        } else {
          this.debe.account = null
          this.debe.account_id = 0   
        }        
      },
      addDebe() {
        if(!this.debe.account_id) {
          this.$awn.alert("No se cargó la Cuenta Contable");
          return false;          
        }

        if(!parseFloat(this.debe.amount)) {
          this.$awn.alert("No se cargó el Importe para la Cuenta Contable");
          return false;          
        }        

        var sum = parseFloat(this.debe.amount)
        this.debe.detail.forEach(element => {
          sum = sum + parseFloat(element.amount)
        });
        if(this.validationMode != 'manual' && sum>parseFloat(this.amountTotal.toFixed(2))) {
          this.$awn.alert("El valor ingresado no puede ser mayor al monto total de la operación");
          return false;                    
        }

        this.debe.detail.push({
          account: this.debe.account,
          account_id: this.debe.account_id,
          amount: this.debe.amount,
        })        

        this.debe.account = null
        this.debe.account_id = 0
        this.debe.amount = 0

        this.getAsiento()
      },
      removeDebe(index) {
        this.debe.detail.splice(index, 1)

        this.getAsiento()
      },

      loadHaberAccounting(object) {
        if(object){
          this.haber.account = object
          this.haber.account_id = object.id
          this.focusAccount('haber')
        } else {
          this.haber.account = null
          this.haber.account_id = 0   
        }        
      },
      addHaber() {
        if(!this.haber.account_id) {
          this.$awn.alert("No se cargó la Cuenta Contable");
          return false;          
        }

        if(!parseFloat(this.haber.amount)) {
          this.$awn.alert("No se cargó el Importe para la Cuenta Contable");
          return false;          
        }        

        var sum = parseFloat(this.haber.amount)
        this.haber.detail.forEach(element => {
          sum = sum + parseFloat(element.amount)
        });        
        if(this.validationMode != 'manual' && sum>parseFloat(this.amountTotal.toFixed(2))) {
          this.$awn.alert("El valor ingresado no puede ser mayor al monto total de la operación");
          return false;                    
        }

        this.haber.detail.push({
          account: this.haber.account,
          account_id: this.haber.account_id,
          amount: this.haber.amount,
        })        

        this.haber.account = null
        this.haber.account_id = 0
        this.haber.amount = 0

        this.getAsiento()
      },
      removeHaber(index) {
        this.haber.detail.splice(index, 1)

        this.getAsiento()
      },   
      
      loadAsiento() {
        if(this.defaultDebe) {
          this.defaultDebe.forEach(element => {  
            if(parseFloat(element.amount) || this.editableAmounts) {

              var statusAdd = true
              this.debe.detail.forEach(element1 => {
                if( element.account.id == element1.account_id && 
                    element.disabled == element1.disabled) {

                  element1.amount = parseFloat(element1.amount) + parseFloat(element.amount)
                  statusAdd = false
                }
              });

              if(statusAdd) {
                this.debe.detail.push({
                  account: element.account,
                  account_id: element.account.id,
                  amount: parseFloat(element.amount),
                  disabled: element.disabled,
                })           
              } 
              
            }   
          });
        }

        if(this.defaultHaber) {
          this.defaultHaber.forEach(element => {
            if(parseFloat(element.amount) || this.editableAmounts) {

              var statusAdd = true
              this.haber.detail.forEach(element1 => {              
                if( element.account.id == element1.account_id && 
                    element.disabled == element1.disabled) {

                  element1.amount = parseFloat(element1.amount) + parseFloat(element.amount)
                  statusAdd = false
                }
              });

              if(statusAdd) {            
                this.haber.detail.push({
                  account: element.account,
                  account_id: element.account.id,
                  amount: parseFloat(element.amount),
                  disabled: element.disabled,
                })             
              }  

            }
          });
        }

        this.getAsiento()
      },
      getAsiento() {
        if(this.validAsiento) {
          var asiento = []
          this.previewDebe.forEach(element => {
            asiento.push({
              account: element.account,
              account_id: element.account_id,
              amount: element.amount,              
              type: 'debe',
            })  
          });
          this.previewHaber.forEach(element => {
            asiento.push({
              account: element.account,
              account_id: element.account_id,
              amount: element.amount,              
              type: 'haber',              
            })  
          });          
          this.$emit('getAsiento', asiento) 
        } else {
          this.$emit('getAsiento', null) 
        }
      }
    }
  }
</script>
<style scoped>
  .entry-side {
    height: 100%;
    background: var(--app-color-surface);
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    overflow: hidden;
  }
  .entry-side__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
  }
  .entry-side--debe .entry-side__header {
    background: var(--app-color-info-soft);
    border-bottom: 2px solid var(--app-color-info);
  }
  .entry-side--haber .entry-side__header {
    background: var(--app-color-success-soft);
    border-bottom: 2px solid var(--app-color-success);
  }
  .entry-side__title {
    font-size: 13px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .entry-side__total {
    font-size: 18px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .entry-side__form {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 10px 12px 0;
  }
  .entry-side__account {
    flex: 1 1 auto;
    min-width: 0;
  }
  .entry-side__amount {
    flex: 0 0 150px;
  }
  .entry-side__add {
    flex: 0 0 auto;
    margin-bottom: 1rem;
  }
  .entry-side__list {
    max-height: 340px;
    overflow-y: auto;
    border-top: 1px solid var(--app-color-border);
  }
  .entry-side__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 6px 7px 12px;
    font-size: 14px;
    font-weight: 600;
    border-bottom: 1px solid var(--app-color-border);
  }
  .entry-side--debe .entry-side__row {
    border-left: 4px solid var(--app-color-info);
  }
  .entry-side--haber .entry-side__row {
    border-left: 4px solid var(--app-color-success);
  }
  .entry-side--debe .entry-side__row--zero,
  .entry-side--haber .entry-side__row--zero {
    font-weight: 400;
    border-left-color: transparent;
  }
  .entry-side__row:last-child {
    border-bottom: 0;
  }
  .entry-side__row--zero .entry-side__name {
    color: var(--app-color-text-muted);
  }
  .entry-side__name {
    flex: 1 1 auto;
    min-width: 0;
  }
  .entry-side__name small {
    display: block;
    color: var(--app-color-text-muted);
    font-size: 11px;
    line-height: 1.2;
  }
  .entry-side__input {
    flex: 0 0 130px;
    width: 130px;
    text-align: right;
    font-weight: 600;
  }
  .entry-side__value {
    font-size: 15px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .entry-side__remove {
    flex: 0 0 auto;
    padding: 2px 4px;
    color: var(--app-color-danger);
  }
  .entry-side__empty {
    margin: 0;
    padding: 10px 12px 14px;
    font-size: 12px;
    color: var(--app-color-text-muted);
  }
  .entry-preview {
    border: 1px solid var(--app-color-border);
    border-radius: 8px;
    overflow: hidden;
  }
  .entry-preview__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 12px;
    background: var(--app-color-surface-subtle);
  }
  .entry-preview__title {
    font-size: 13px;
    font-weight: 700;
  }
  .entry-preview__state {
    padding: 2px 10px;
    font-size: 12px;
    font-weight: 700;
    border-radius: 999px;
  }
  .entry-preview__state--ok {
    color: var(--app-color-success);
    background: var(--app-color-success-soft);
  }
  .entry-preview__state--error {
    color: var(--app-color-danger);
    background: var(--app-color-danger-soft);
  }
  .entry-preview__state--info {
    color: var(--app-color-info);
    background: var(--app-color-info-soft);
  }
  .entry-preview__totals td {
    font-weight: 700;
    border-top: 2px solid var(--app-color-border-strong);
  }
  @media (max-width: 575.98px) {
    .entry-side__form {
      flex-wrap: wrap;
    }
    .entry-side__account {
      flex-basis: 100%;
    }
    .entry-side__amount {
      flex: 1 1 auto;
    }
    .entry-side__input {
      flex-basis: 110px;
      width: 110px;
    }
  }
</style>

